import {
  Button,
  Card,
  Field,
  Heading,
  Paragraph,
  Textfield,
} from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { type PropsWithChildren, useEffect, useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { InnstillingerDto } from "@/api/generated";
import { BestillingFormErrorSummary } from "@/components/forms/FormErrors";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { formatOrgNr } from "@/utils";
import { useAuth } from "../auth";
import { useSelskaper } from "../selskap/SelskapContext";
import { useToaster } from "../toaster";
import { useInnstillingerData } from "./innstillingerData";
import {
  cleanKontonummer,
  initFormValues,
  innstillingerFormSchema,
  type innstillingerFormValues,
} from "./innstillingerFormSchemas";

const DescriptionTerm = ({ felt, value }: { felt: string; value: string }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2 mb-2 md:mb-0">
      <dt className="font-semibold">{felt}:</dt>
      <dd className="m-0 whitespace-pre-line break-all text-pretty">
        <span>{value}</span>
      </dd>
    </div>
  );
};

const WarningCardIfMissingInnstillinger = ({
  manglerInnstillinger,
  children,
}: PropsWithChildren<{ manglerInnstillinger: boolean }>) => {
  if (manglerInnstillinger) {
    return (
      <Card variant={"tinted"} className="bg-balder-background-warning m-0">
        {children}
      </Card>
    );
  }
  return <div>{children}</div>;
};

export const InnstillingerForm = ({
  innstillinger,
}: {
  innstillinger: InnstillingerDto | null | undefined;
}) => {
  const { t } = useTranslation();
  const { active } = useSelskaper();
  const toast = useToaster();
  const { account } = useAuth();
  const [errorsFromBackend, setErrorsFromBackend] = useState<
    Record<string, string>
  >({});

  const {
    mutate: { mutateInnstillingerAsync, isPending },
  } = useInnstillingerData(active?.organisasjonsnummer);

  const form = useForm({
    resolver: zodResolver(innstillingerFormSchema),
    values: initFormValues(innstillinger),
    mode: "onSubmit",
  });

  async function handleSubmit(values: innstillingerFormValues) {
    setErrorsFromBackend({});
    if (!active?.organisasjonsnummer || !account?.pid) {
      return;
    }
    await mutateInnstillingerAsync({
      body: {
        fakturaEpost: values.fakturaEpost,
        norgesprisKontonummer: cleanKontonummer(values.norgesprisKontonummer),
        strømstønadKontonummer: cleanKontonummer(values.strømstønadKontonummer),
        varmeSelskapOrganisasjonsnummer: active.organisasjonsnummer,
      },
      path: { organisasjonsNummer: active.organisasjonsnummer },
    })
      .then(async (_) => {
        toast.success({ messageKey: "pages.innstillinger.toast.lagret" });
      })
      .catch((error) => {
        const errors = error.response?.data?.errors;
        if (Object.values(errors || {})?.length > 0) {
          // Vi fikk valideringsfeil fra backend, vis feilene i skjemaet
          setErrorsFromBackend(errors || {});
          return;
        }
        // En ikke-valideringsfeil oppstå, la oss logge og vise en generisk feilmelding
        console.error("Feil ved innsending av innstillinger", error);
        toast.error({ messageKey: "pages.innstillinger.toast.feil" });
      });
  }

  const epostFeltId = useId();
  const norgesprisKontonummerFeltId = useId();
  const strømstønadKontonummerFeltId = useId();

  const manglerInnstillinger = !innstillinger;

  useEffect(() => {
    // Add errors from backend to form state
    const formKeys = Object.keys(
      form.getValues(),
    ) as (keyof innstillingerFormValues)[];

    for (const backendKey in errorsFromBackend) {
      // Normalize backend key by lowercasing the first character (FakturaEpost -> fakturaEpost)
      const normalizedBackendKey =
        backendKey.charAt(0).toLowerCase() + backendKey.slice(1);

      // Try to find a matching form key:
      // 1) exact match with normalized backend key
      // 2) full case-insensitive match as a fallback
      const match = formKeys.find(
        (k) =>
          k === (normalizedBackendKey as keyof innstillingerFormValues) ||
          String(k).toLowerCase() === backendKey.toLowerCase(),
      );

      if (match && !(match in form.formState.errors)) {
        form.setError(match, {
          type: "custom",
          message: errorsFromBackend[backendKey][0],
        });
      }
    }
  }, [errorsFromBackend, form.formState.errors, form.getValues, form.setError]);

  return (
    <FormProvider {...form}>
      <FormWrapper
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <Heading level={2} data-size="xs" className="m-0">
          {t("pages.innstillinger.form.tittel")}
        </Heading>
        <dl className="m-0">
          <DescriptionTerm
            felt={t("pages.innstillinger.navn")}
            value={active?.navn || ""}
          />
          <DescriptionTerm
            felt={t("pages.innstillinger.organisasjonsnummer")}
            value={formatOrgNr(active?.organisasjonsnummer) || ""}
          />
          {innstillinger?.endretSistAvNavn && (
            <DescriptionTerm
              felt={t("pages.innstillinger.sist-endret-av")}
              value={innstillinger?.endretSistAvNavn}
            />
          )}
        </dl>

        <WarningCardIfMissingInnstillinger
          manglerInnstillinger={manglerInnstillinger}
        >
          <Field>
            <Textfield
              id={epostFeltId}
              type="email"
              {...form.register("fakturaEpost")}
              label={
                <span className="flex items-center gap-1">
                  <Heading level={3} data-size="xs">
                    {t("pages.innstillinger.felt.fakturaEpost.label")}
                  </Heading>
                  <Paragraph data-size="sm" className="mt-1">
                    {t("pages.innstillinger.felt.fakturaEpost.hjelpetekst")}
                  </Paragraph>
                </span>
              }
              error={
                form.formState.errors.fakturaEpost?.message && (
                  <span>{t(form.formState.errors.fakturaEpost?.message)}</span>
                )
              }
            />
          </Field>
        </WarningCardIfMissingInnstillinger>
        <WarningCardIfMissingInnstillinger
          manglerInnstillinger={manglerInnstillinger}
        >
          <Field>
            <Textfield
              id={norgesprisKontonummerFeltId}
              type="text"
              {...form.register("norgesprisKontonummer")}
              maxLength={13}
              label={
                <span className="flex items-center gap-1">
                  <Heading level={3} data-size="xs">
                    {t("pages.innstillinger.felt.norgesprisKontonummer.label")}
                  </Heading>
                  <Paragraph data-size="sm" className="mt-1">
                    {t(
                      "pages.innstillinger.felt.norgesprisKontonummer.hjelpetekst",
                    )}
                  </Paragraph>
                </span>
              }
              error={
                form.formState.errors.norgesprisKontonummer?.message && (
                  <span>
                    {t(form.formState.errors.norgesprisKontonummer?.message)}
                  </span>
                )
              }
            />
          </Field>
        </WarningCardIfMissingInnstillinger>
        <WarningCardIfMissingInnstillinger
          manglerInnstillinger={manglerInnstillinger}
        >
          <Field>
            <Textfield
              id={strømstønadKontonummerFeltId}
              type="text"
              {...form.register("strømstønadKontonummer")}
              maxLength={13}
              label={
                <span className="flex items-center gap-1">
                  <Heading level={3} data-size="xs">
                    {t("pages.innstillinger.felt.stromstonadKontonummer.label")}
                  </Heading>
                  <Paragraph data-size="sm" className="mt-1">
                    {t(
                      "pages.innstillinger.felt.stromstonadKontonummer.hjelpetekst",
                    )}
                  </Paragraph>
                </span>
              }
              error={
                form.formState.errors.strømstønadKontonummer?.message && (
                  <span>
                    {t(form.formState.errors.strømstønadKontonummer?.message)}
                  </span>
                )
              }
            />
          </Field>
        </WarningCardIfMissingInnstillinger>

        <BestillingFormErrorSummary errors={form.formState.errors} />

        <div className="flex gap-4">
          <Button
            variant="secondary"
            type="button"
            loading={isPending}
            disabled={isPending}
            asChild
          >
            <Link to="/">{t("button.cancel")}</Link>
          </Button>
          <Button
            type="submit"
            loading={form.formState.isSubmitting || isPending}
            disabled={form.formState.isSubmitting || isPending}
          >
            {form.formState.isSubmitting
              ? t("button.saving")
              : t("button.save")}
          </Button>
        </div>
      </FormWrapper>
    </FormProvider>
  );
};
